// Buckets a list of ISO timestamps into fixed-width, fixed-count time windows ending "now",
// for lightweight sparkline/trend visualization computed entirely from data the frontend
// already fetches (e.g. the exchanges/executions arrays already loaded for KPI counts) — no
// new backend endpoint required.
//
// Caveat this is deliberately honest about: the source arrays are page-size-capped (e.g. the
// 100 most recent exchanges, 50 most recent executions), not a full historical query. Older
// buckets can under-count if actual volume in that window exceeded the fetch's page size.
// That's an acceptable approximation for a KPI sparkline, not for anything billed as exact.
export function bucketCountsByTime(
  timestamps: Array<string | undefined>,
  bucketCount: number,
  bucketDurationMs: number,
  now: Date = new Date(),
): number[] {
  const buckets = new Array(bucketCount).fill(0) as number[];
  const endMs = now.getTime();
  const startMs = endMs - bucketCount * bucketDurationMs;

  for (const timestamp of timestamps) {
    if (!timestamp) continue;
    const ms = new Date(timestamp).getTime();
    if (Number.isNaN(ms) || ms < startMs || ms > endMs) continue;
    const index = Math.min(bucketCount - 1, Math.floor((ms - startMs) / bucketDurationMs));
    buckets[index] += 1;
  }

  return buckets;
}

export const ONE_HOUR_MS = 60 * 60 * 1000;
export const ONE_DAY_MS = 24 * ONE_HOUR_MS;

export type TrendDirection = 'up' | 'down' | 'flat';

export interface Trend {
  direction: TrendDirection;
  changePercent: number | null;
}

// Compares the most recent bucket to the one before it — "vs. prior period" in the most
// literal reading of that phrase, using whatever bucket granularity the caller chose.
export function computeTrend(buckets: number[]): Trend {
  if (buckets.length < 2) return { direction: 'flat', changePercent: null };
  const current = buckets[buckets.length - 1];
  const previous = buckets[buckets.length - 2];

  if (previous === 0) {
    if (current === 0) return { direction: 'flat', changePercent: null };
    return { direction: 'up', changePercent: null };
  }

  const changePercent = ((current - previous) / previous) * 100;
  if (Math.abs(changePercent) < 1) return { direction: 'flat', changePercent };
  return { direction: changePercent > 0 ? 'up' : 'down', changePercent };
}
